import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * Scroll reveal that is VISIBLE BY DEFAULT (design audit R58, PR group 5).
 *
 * The old sections used framer-motion `initial={{ opacity: 0 }}` +
 * `whileInView`, so any capture that does not scroll (a full-page
 * screenshot, a crawler that renders but does not scroll, a print) saw blank
 * sections. Here the content is rendered visible; only a block that starts
 * below the fold is hidden for the fade-in, and it is always shown again when
 * it scrolls into view, after FAILSAFE_MS, or on print. If
 * IntersectionObserver is missing or the visitor prefers reduced motion, it
 * is never hidden at all. The text is in the DOM in every case.
 *
 * It accepts (and ignores) the framer-motion props the sections used to pass,
 * so the call sites changed only by import. `transition` still supplies the
 * duration and delay.
 */
export const REVEAL_FAILSAFE_MS = 2500;

interface RevealProps extends React.HTMLAttributes<HTMLDivElement> {
  transition?: { duration?: number; delay?: number };
  // Accepted for call-site compatibility with the old motion props; unused.
  initial?: unknown;
  whileInView?: unknown;
  viewport?: unknown;
  variants?: unknown;
}

export const Reveal: React.FC<RevealProps> = ({
  children,
  transition,
  style,
  initial: _initial,
  whileInView: _whileInView,
  viewport: _viewport,
  variants: _variants,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    try {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    } catch {
      /* ignore */
    }
    if (el.getBoundingClientRect().top < window.innerHeight) return;

    setHidden(true);
    const show = () => setHidden(false);
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          show();
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    const timer = window.setTimeout(show, REVEAL_FAILSAFE_MS);
    window.addEventListener('beforeprint', show);
    return () => {
      io.disconnect();
      window.clearTimeout(timer);
      window.removeEventListener('beforeprint', show);
    };
  }, []);

  const duration = transition?.duration ?? 0.5;
  const delay = transition?.delay ?? 0;

  return (
    <div
      ref={ref}
      data-reveal={hidden ? 'hidden' : 'visible'}
      style={{
        ...style,
        opacity: hidden ? 0 : 1,
        transform: hidden ? 'translateY(24px)' : 'none',
        transition: `opacity ${duration}s ease-out ${delay}s, transform ${duration}s ease-out ${delay}s`,
      }}
      {...rest}
    >
      {children}
    </div>
  );
};
